/**
 * Okendo reviews inside the UpCart (AfterSell) cart drawer.
 *
 * UpCart renders its drawer client-side (light DOM) and re-renders on every
 * change, so the theme's cart markup can't carry review widgets. Instead we:
 *   1. Per line item: inject an Okendo star rating (data-oke-star-rating),
 *      mapped to that item's product via its handle from upcartGetCart().
 *   2. Once per drawer: inject an Okendo reviews carousel (data-oke-carousel)
 *      just above the drawer footer (above the checkout button).
 *   3. Initialise the injected widgets with window.okeWidgetApi.initWidget,
 *      which renders Okendo widgets added to the DOM after page load.
 *
 * We hook UpCart's public *subscribe* API (upcartSubscribeCart*) rather than
 * overwriting the upcartOn* globals, because those are already used by other
 * apps on this store (e.g. Seel). Injections are idempotent + re-run on every
 * cart event so they survive UpCart's client re-renders.
 *
 * Docs: UpCart API https://intercom.help/as-upcart/en/articles/10561853
 *       Okendo JS API window.okeWidgetApi.initWidget(el, forceReinit)
 */
(function () {
  'use strict';

  var CAROUSEL_HEADING = 'What Families Are Saying';
  var STAR_CLASS = 'upcart-okendo-star';
  var CAROUSEL_CLASS = 'upcart-okendo-carousel';

  function whenOkeReady(cb) {
    if (window.okeWidgetApi && typeof window.okeWidgetApi.initWidget === 'function') return cb();
    var tries = 0;
    var t = setInterval(function () {
      if (window.okeWidgetApi && typeof window.okeWidgetApi.initWidget === 'function') {
        clearInterval(t);
        cb();
      } else if (++tries > 60) {
        clearInterval(t); // give up after ~9s
      }
    }, 150);
  }

  function initWidget(el) {
    whenOkeReady(function () {
      try { window.okeWidgetApi.initWidget(el, true); } catch (e) { /* ignore */ }
    });
  }

  // Okendo's carousel is a Vue "Hooper" slider whose itemsToShow is chosen from the
  // WINDOW width, so in the narrow drawer (on a desktop window) it tries to show
  // several slides. Our CSS forces each slide to full width, so Hooper must show
  // exactly one or its translate math desyncs. Poll for the drawer's own Hooper Vue
  // instance and pin itemsToShow = 1. Scoped to .upcart-okendo-carousel so the
  // homepage carousel is never touched.
  function tuneCartCarousel() {
    var tries = 0;
    var t = setInterval(function () {
      var el = document.querySelector('.' + CAROUSEL_CLASS + ' .oke-reviewCarousel .hooper') ||
               document.querySelector('.' + CAROUSEL_CLASS + ' .hooper');
      var vi = el && el.__vue__;
      if (vi && vi.initialized) {
        clearInterval(t);
        try {
          vi.displayConfig.itemsToShow = 1;
          if (vi.settings) vi.settings.itemsToShow = 1;
          vi.update();
        } catch (e) { /* ignore */ }
      } else if (++tries > 60) {
        clearInterval(t); // give up after ~9s
      }
    }, 150);
  }

  function getItems() {
    try {
      var c = window.upcartGetCart && window.upcartGetCart();
      return (c && c.items) || [];
    } catch (e) { return []; }
  }

  function handleFromHref(href) {
    var m = href && href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function injectStars() {
    var items = getItems();
    var idByHandle = {};
    items.forEach(function (it) {
      if (it && it.handle && it.productId && !it.isRewardItem) idByHandle[it.handle] = it.productId;
    });

    document.querySelectorAll('.upcart-public-cart-item').forEach(function (line) {
      var info = line.querySelector('.upcart-public-component-product-tile__info') || line;
      var link = line.querySelector('.upcart-public-component-product-tile__title-link') ||
                 line.querySelector('a[href*="/products/"]');
      var handle = link ? handleFromHref(link.getAttribute('href')) : null;
      var productId = handle ? idByHandle[handle] : null;
      if (!productId) return;

      var pid = 'shopify-' + productId;
      var existing = info.querySelector('.' + STAR_CLASS);
      if (existing) {
        if (existing.getAttribute('data-oke-reviews-product-id') === pid) return; // already correct
        existing.remove(); // DOM reused for a different product — replace
      }

      var star = document.createElement('div');
      star.className = STAR_CLASS;
      star.setAttribute('data-oke-star-rating', '');
      star.setAttribute('data-oke-reviews-product-id', pid);

      var variant = info.querySelector('.upcart-public-component-product-tile__variant');
      if (variant) variant.insertAdjacentElement('afterend', star);
      else info.appendChild(star);

      initWidget(star);
    });
  }

  function injectCarousel() {
    // Place the card directly ABOVE the "You May Also Like" upsell; fall back to
    // just above the footer if this drawer has no upsell.
    var upsell = document.querySelector('.upcart-public-upsell');
    var footer = document.querySelector('.upcart-footer');
    var anchor = upsell || footer;
    if (!anchor || !anchor.parentNode) return;
    var existing = document.querySelector('.' + CAROUSEL_CLASS);
    if (existing) {
      // Reposition if the upsell rendered after our first insert (upsells load async).
      if (existing.nextElementSibling !== anchor || existing.parentNode !== anchor.parentNode) {
        anchor.parentNode.insertBefore(existing, anchor);
      }
      return;
    }

    var wrap = document.createElement('div');
    wrap.className = CAROUSEL_CLASS;

    // Collapsed by default: a compact tappable header that expands to reveal
    // the reviews carousel. The Okendo carousel is only initialised on first
    // expand (lazy) so it costs nothing until the shopper opts in.
    var starSvg = '<svg class="' + CAROUSEL_CLASS + '__star" viewBox="0 0 20 20" width="13" height="13" aria-hidden="true">' +
      '<path d="M10 1.6l2.47 5.01 5.53.8-4 3.9.94 5.51L10 14.22 5.06 16.82l.94-5.51-4-3.9 5.53-.8z"/></svg>';
    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = CAROUSEL_CLASS + '__toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.innerHTML =
      '<span class="' + CAROUSEL_CLASS + '__stars" aria-hidden="true">' + starSvg + starSvg + starSvg + starSvg + starSvg + '</span>' +
      '<span class="' + CAROUSEL_CLASS + '__label">' + CAROUSEL_HEADING + '</span>' +
      '<span class="' + CAROUSEL_CLASS + '__chevron" aria-hidden="true"></span>';

    var body = document.createElement('div');
    body.className = CAROUSEL_CLASS + '__body';
    body.hidden = true;
    var carousel = document.createElement('div');
    carousel.setAttribute('data-oke-carousel', '');
    carousel.setAttribute('data-oke-header-text', '');
    body.appendChild(carousel);

    toggle.addEventListener('click', function () {
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      body.hidden = open;
      if (!open && !carousel.dataset.okeInit) {
        carousel.dataset.okeInit = '1';
        initWidget(carousel); // lazy init on first expand
        tuneCartCarousel();    // pin the drawer slider to one full-width card
      }
    });

    wrap.appendChild(toggle);
    wrap.appendChild(body);
    // Insert before the ANCHOR (upsell, or footer as fallback), not `footer`
    // directly: an empty cart can render an upsell but no .upcart-footer, so
    // `footer` is null there even though the guard above passed on `anchor`.
    anchor.parentNode.insertBefore(wrap, anchor);
  }

  var scheduled = false;
  function run() {
    scheduled = false;
    injectStars();
    injectCarousel();
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // Let UpCart finish its render before we inject.
    requestAnimationFrame(function () { setTimeout(run, 60); });
  }

  function hook() {
    [
      'upcartSubscribeCartLoaded',
      'upcartSubscribeCartUpdated',
      'upcartSubscribeCartOpened',
      'upcartSubscribeAddedToCart',
      'upcartSubscribeItemRemoved',
      'upcartSubscribeUpsellsRendered'
    ].forEach(function (fn) {
      if (typeof window[fn] === 'function') {
        try { window[fn](schedule); } catch (e) { /* ignore */ }
      }
    });
    schedule();
  }

  function ensureStyles() {
    if (document.getElementById('okendo-upcart-styles')) return;
    var s = document.createElement('style');
    s.id = 'okendo-upcart-styles';
    var C = CAROUSEL_CLASS, S = STAR_CLASS;
    s.textContent =
      // Per-item star rating: compact, tucked under the variant line.
      // !important + descendant selectors: Okendo's own CSS pins font-size:16px on
      // the hydrated .okeReviews root (plain inheritance from the container loses),
      // and its star SVGs ship a hardcoded height="18" attribute.
      '.' + S + '{margin:3px 0 1px;line-height:1;}' +
      '.' + S + ' [data-oke-star-rating]{font-size:12px;}' +
      '.' + S + ' .okeReviews{font-size:12px !important;}' +
      '.' + S + ' .oke-sr-count{font-size:12px !important;line-height:1.2 !important;}' +
      '.' + S + ' .oke-sr-stars svg{height:13px !important;}' +
      // Social-proof card. HIDDEN BY DEFAULT and gated for a future A/B test: it's
      // unproven whether brand testimonials this far down the funnel help or distract.
      // To run the "on" variant, override to `display:block` (e.g. via Intelligems) or
      // delete the `display:none` below. The per-item star ratings are separate and stay.
      '.' + C + '{display:none;margin:6px 14px 14px;border-radius:10px;overflow:hidden;background:#fff;' +
        'box-shadow:0 0 4px rgba(0,0,0,.18);}' +
      '.' + C + '__toggle{display:flex;align-items:center;gap:10px;width:100%;box-sizing:border-box;' +
        'background:transparent;border:0;padding:14px 16px;margin:0;cursor:pointer;text-align:left;color:#4A2A34;}' +
      '.' + C + '__stars{display:inline-flex;gap:2px;flex:0 0 auto;}' +
      '.' + C + '__star{fill:#E26D8A;display:block;}' +
      '.' + C + '__label{flex:1 1 auto;font-family:var(--typeHeaderPrimary),var(--typeHeaderFallback);' +
        'font-size:16px;font-weight:500;line-height:1.15;letter-spacing:.01em;color:#4A2A34;}' +
      '.' + C + '__chevron{flex:0 0 auto;width:9px;height:9px;margin-right:2px;' +
        'border-right:2px solid #C86E86;border-bottom:2px solid #C86E86;opacity:.85;' +
        'transform:rotate(45deg);transition:transform .25s ease;}' +
      '.' + C + '__toggle[aria-expanded="true"] .' + C + '__chevron{transform:rotate(-135deg);}' +
      '.' + C + '__body{padding:2px 14px 14px;overflow-x:hidden;}' +
      '.' + C + '__body:not([hidden]){animation:upcartOkeReveal .28s ease both;}' +
      '.' + C + '__body [data-oke-carousel]{--oke-star-rating-size:14px;font-size:13px;}' +
      // The cart file owns the drawer carousel's look end to end so it renders the
      // SAME on every page. The homepage styling block emits GLOBAL rules that also
      // match this carousel; the worst is a 5-column slide width (specificity 0,4,2
      // + !important) that crushes the drawer into slivers. We neutralise it by
      // mirroring that exact selector chain PLUS the drawer scope (0,5,2), which wins
      // wherever the drawer opens. Hooper itemsToShow is forced to 1 in JS to match.
      // Hide the redundant aggregate header — our toggle already shows the rating.
      '.' + C + ' .oke-reviewCarousel-header{display:none !important;}' +
      // One full-width testimonial card per view.
      // Track padding MUST stay 0: Hooper translates by the full list width per slide,
      // so any track padding shrinks the visible slide and the carousel drifts a few px
      // per "next" click. The __body already insets the whole carousel by 14px.
      '.' + C + ' .oke-reviewCarousel-reviews.hooper ul.hooper-track{padding:0 !important;gap:0 !important;}' +
      '.' + C + ' .oke-reviewCarousel-reviews.hooper ul.hooper-track li.hooper-slide{width:100% !important;max-width:100% !important;background:#fff !important;border-radius:14px !important;box-shadow:0 1px 6px rgba(74,42,52,.14) !important;overflow:hidden !important;padding:0 !important;}' +
      // Tall enough for the tallest possible card: photo (175) + name + stars + title +
      // a 2-line-clamped body + padding (~368px). Sized to the clamp so nothing clips.
      '.' + C + ' .hooper{height:378px !important;}' +
      '.' + C + ' .hooper-list{overflow:hidden !important;}' +
      // Card inner: photo full-bleed on top at a capped height (a 3:4 source would
      // tower in the full-width drawer card), rating + quote + author centered below.
      '.' + C + ' .oke-reviewCardFeaturedMedia,.' + C + ' .oke-reviewCardFeaturedMedia--verticalStretch{height:auto !important;padding:0 !important;text-align:center;}' +
      // Okendo nests media/link wrappers that size to the source (leaving a tall gap
      // that pushes the text off the clipped slide), so cap the whole chain to 175px.
      '.' + C + ' .oke-reviewCardFeaturedMedia-media,.' + C + ' .oke-media,.' + C + ' .oke-media--linked,.' + C + ' .oke-media-link{width:100% !important;max-width:100% !important;height:175px !important;max-height:175px !important;display:block !important;margin:0 !important;overflow:hidden !important;border-radius:0 !important;aspect-ratio:auto !important;}' +
      '.' + C + ' .oke-media-image,.' + C + ' .oke-reviewCardFeaturedMedia-media img{width:100% !important;height:100% !important;max-height:175px !important;object-fit:cover !important;border-radius:0 !important;}' +
      // Text block under the photo: reviewer name first, then rating + quote, centered.
      '.' + C + ' .oke-reviewCardFeaturedMedia-container{display:flex !important;flex-direction:column !important;height:auto !important;padding:12px 14px 14px !important;gap:6px !important;}' +
      '.' + C + ' .oke-reviewCardFeaturedMedia-content,.' + C + ' .oke-reviewContainer,.' + C + ' .oke-reviewContent{text-align:center !important;}' +
      // Okendo natively truncates a long review body to ~3 lines (with its own Read More,
      // which we hide); the .hooper height above is sized to fit that tallest 3-line card
      // so nothing clips. Full reviews live on the reviews page.
      '.' + C + ' .oke-reviewContent-readMore{display:none !important;}' +
      '.' + C + ' .oke-reviewCardFeaturedMedia-head{order:-1 !important;justify-content:center !important;margin:0 !important;padding:0 !important;text-align:center !important;}' +
      '.' + C + ' .oke-w-reviewer-name::before{display:none !important;}' +
      '.' + C + ' .oke-w-review-footer{margin:6px 0 0 !important;}' +
      '.' + C + ' .hooper-navigation{position:absolute !important;top:88px !important;}' +
      '@keyframes upcartOkeReveal{from{opacity:0;transform:translateY(4px);}to{opacity:1;transform:none;}}' +
      '@media (prefers-reduced-motion:reduce){.' + C + '__body:not([hidden]){animation:none;}}';
    document.head.appendChild(s);
  }

  function boot() {
    ensureStyles();
    if (window.upcartSubscribeCartLoaded) { hook(); return; }
    // UpCart may initialise after us — poll briefly.
    var tries = 0;
    var t = setInterval(function () {
      if (window.upcartSubscribeCartLoaded) { clearInterval(t); hook(); }
      else if (++tries > 50) clearInterval(t); // give up after ~10s
    }, 200);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
